import { HighlightType } from '../types/profile';

/**
 * Identity for a stored highlight. Image uploads often share an empty link and
 * caption, so imagePath is what tells two photos apart. Media url does the
 * same for Spotify, YouTube, and SoundCloud.
 */
export function getHighlightIdentity(highlight?: HighlightType | null): string {
    if (!highlight) return '';
    if (highlight.isVirtualJamBoardHighlight || highlight.typeCd === 'JB') {
        return [
            'jb',
            highlight.jamBoardPinId || '',
            highlight.description || '',
            highlight.coverArtUrl || '',
        ].join('\0');
    }
    return [
        highlight.typeCd || '',
        highlight.imagePath || '',
        highlight.mediaUrl || '',
        highlight.url || '',
        highlight.coverArtUrl || '',
        highlight.description || '',
    ].join('\0');
}

export function findHighlightIndex(
    highlights: HighlightType[] | null | undefined,
    selected?: HighlightType | null
): number {
    if (!highlights?.length || !selected) return -1;
    const byReference = highlights.indexOf(selected);
    if (byReference !== -1) return byReference;
    const identity = getHighlightIdentity(selected);
    if (!identity) return -1;
    return highlights.findIndex((item) => getHighlightIdentity(item) === identity);
}

/** Stable React key. Index is only a tiebreaker when the highlight has no stored id. */
export function getHighlightReactKey(highlight: HighlightType, index: number): string {
    const identity = getHighlightIdentity(highlight);
    if (highlight.imagePath || highlight.mediaUrl || highlight.jamBoardPinId) {
        return identity;
    }
    return `${identity}#${index}`;
}

function sameStoredHighlight(left: HighlightType, right: HighlightType): boolean {
    if (left.imagePath && right.imagePath && left.imagePath === right.imagePath) {
        return true;
    }
    if (
        left.mediaUrl &&
        right.mediaUrl &&
        left.mediaUrl === right.mediaUrl &&
        (left.typeCd || '') === (right.typeCd || '')
    ) {
        return true;
    }
    return false;
}

/**
 * Keep the image URL the carousel is already showing when the saved file did
 * not change. Profile saves re-sign every highlight image, and swapping those
 * URLs reloads the photos.
 */
export function mergeSavedHighlightImages(
    previous: HighlightType[] | null | undefined,
    incoming: HighlightType[] | null | undefined
): HighlightType[] | null | undefined {
    if (!Array.isArray(incoming)) return previous;
    const previousList = previous || [];
    return incoming.map((item) => {
        const match = previousList.find((prev) => sameStoredHighlight(prev, item));
        if (!match?.image?.url || match.image.file) return item;
        return { ...item, image: { url: match.image.url } };
    });
}
